import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Trash2 } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnifiedReviewsManagement = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all'); // all, vehicle, general, service, dealership

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateReviewStatus = async (reviewId, status) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/status?status=${status}`);
      toast.success(t('admin.statusUpdated'));
      fetchReviews();
    } catch (error) {
      console.error('Error updating review status:', error);
      toast.error(t('admin.statusUpdateFailed'));
    }
  };

  const deleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success(t('admin.statusUpdated'));
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error(t('admin.statusUpdateFailed'));
    }
  };

  const getReviewTypeLabel = (reviewType) => {
    switch (reviewType) {
      case 'vehicle': return t('admin.vehicleReviewType');
      case 'general': return t('admin.generalReviewType');
      case 'service': return t('admin.serviceReviewType');
      case 'dealership': return t('admin.dealershipReviewType');
      default: return reviewType;
    }
  };

  const filteredReviews = filterType === 'all' 
    ? reviews 
    : reviews.filter(r => r.review_type === filterType);

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
          {t('admin.allReviews')}
        </h2>
        
        {/* Filter buttons */}
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant={filterType === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterType('all')}
          >
            {t('common.all') || 'All'}
          </Button>
          <Button
            size="sm"
            variant={filterType === 'vehicle' ? 'default' : 'outline'}
            onClick={() => setFilterType('vehicle')}
          >
            {t('admin.vehicleReviewType')}
          </Button>
          <Button
            size="sm"
            variant={filterType === 'general' ? 'default' : 'outline'}
            onClick={() => setFilterType('general')}
          >
            {t('admin.generalReviewType')}
          </Button>
          <Button
            size="sm"
            variant={filterType === 'service' ? 'default' : 'outline'}
            onClick={() => setFilterType('service')}
          >
            {t('admin.serviceReviewType')}
          </Button>
          <Button
            size="sm"
            variant={filterType === 'dealership' ? 'default' : 'outline'}
            onClick={() => setFilterType('dealership')}
          >
            {t('admin.dealershipReviewType')}
          </Button>
        </div>
      </div>

      <div className="rounded-lg shadow-md overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="reviews-table">
            <thead className="border-b" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.customerName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.reviewType')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.rating')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.review')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {filteredReviews.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                    {t('admin.noReviewsFound')}
                  </td>
                </tr>
              ) : (
                filteredReviews.map(review => (
                  <tr key={review.id}>
                    <td className="px-6 py-4">
                      <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{review.customer_name}</p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{review.customer_email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline">
                        {getReviewTypeLabel(review.review_type)}
                      </Badge>
                      {review.vehicle_id && (
                        <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                          ID: {review.vehicle_id}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                          />
                        ))}
                        <span className="ml-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          ({review.rating}/5)
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <p className="truncate" style={{ color: 'var(--text-secondary)' }}>{review.comment}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={review.status === 'approved' ? 'default' : review.status === 'pending' ? 'secondary' : 'destructive'}>
                        {t(`admin.${review.status}`)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        {review.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              onClick={() => updateReviewStatus(review.id, 'approved')}
                              data-testid={`approve-review-${review.id}`}
                            >
                              {t('admin.approve')}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              onClick={() => updateReviewStatus(review.id, 'rejected')}
                              data-testid={`reject-review-${review.id}`}
                            >
                              {t('admin.reject')}
                            </Button>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => deleteReview(review.id)}
                          data-testid={`delete-review-${review.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Analytics Statistics Component

export default UnifiedReviewsManagement;
